import Phaser from 'phaser';
import { itemManager } from '../../managers/ItemManager';

export default class WeaponUI extends Phaser.GameObjects.Container {
    constructor(scene, x = 0, y = 0) {
        super(scene, x, y);
        this.scene = scene;
        
        // Debug mode state
        this.debugMode = false;
        
        // Weapon slot states (display only)
        this.equippedSlot1 = null; // Left click slot - for display
        this.equippedSlot2 = null; // Right click slot - for display
        
        // Weapon icons
        this.slot1Icon = null;
        this.slot2Icon = null;
        
        // Weapon cooldowns
        this.slot1Cooldown = 0;
        this.slot2Cooldown = 0;
        
        // UI dimensions and positions
        this.viewportWidth = 256;
        this.viewportHeight = 192;
        this.weaponUIWidth = 77;
        this.weaponUIHeight = 44;
        this.slotSize = 27;
        
        // Calculate positions (weapon-ui.png centered at bottom left)
        this.weaponUIX = this.viewportWidth / 2 - 90; // 38
        this.weaponUIY = 170;
        
        // Weapon slot positions (relative to weapon UI center)
        this.slot1X = this.weaponUIX - 16;
        this.slot1Y = this.weaponUIY;
        this.slot2X = this.weaponUIX + 16;
        this.slot2Y = this.weaponUIY;
        
        // Transparency control
        this.currentAlpha = 1.0;
        this.targetAlpha = 1.0;
        this.isTransparent = false;
        
        this.createUI();
        this.setupTransparencySystem();
        this.setupTooltip();
        this.equipDefaultWeapons();
        
        scene.add.existing(this);
        
        // Set high depth to appear on top and fix to screen coordinates
        this.setDepth(500);
        this.setScrollFactor(0);
    }
    
    createUI() {
        // Create the main weapon UI background 
        this.weaponUIBg = this.scene.add.image(this.weaponUIX, this.weaponUIY, 'tool-ui'); // Reuse tool-ui texture
        this.add(this.weaponUIBg);
        
        // Create hover overlays (initially invisible)
        this.slot1Hover = this.scene.add.image(this.slot1X, this.slot1Y, 'tool-ui-icon-empty-hover');
        this.slot1Hover.setVisible(false);
        this.slot1Hover.setDepth(508);
        this.slot1Hover.setScrollFactor(0);
        
        this.slot2Hover = this.scene.add.image(this.slot2X, this.slot2Y, 'tool-ui-icon-empty-hover');
        this.slot2Hover.setVisible(false);
        this.slot2Hover.setDepth(508);
        this.slot2Hover.setScrollFactor(0);
        
        // Create debug highlight rectangles
        this.debugGraphics = this.scene.add.graphics();
        this.debugGraphics.setScrollFactor(0);
        this.debugGraphics.setDepth(550);
        this.updateDebugVisuals();
        
        // Create interactive areas for equipped weapon slots
        this.createSlotInteractiveAreas();
    }
    
    createSlotInteractiveAreas() {
        // Create invisible interactive areas over weapon slots
        this.slot1Area = this.scene.add.rectangle(this.slot1X, this.slot1Y, this.slotSize, this.slotSize, 0x000000, 0);
        this.slot1Area.setInteractive();
        this.slot1Area.setDepth(512);
        this.slot1Area.setScrollFactor(0);
        this.slot1Area.slotNumber = 1;
        
        this.slot2Area = this.scene.add.rectangle(this.slot2X, this.slot2Y, this.slotSize, this.slotSize, 0x000000, 0);
        this.slot2Area.setInteractive();
        this.slot2Area.setDepth(512);
        this.slot2Area.setScrollFactor(0);
        this.slot2Area.slotNumber = 2;
        
        // Setup hover events
        this.setupHoverEvents();
        
        // Setup click events for weapon usage (not equipping)
        this.setupClickEvents();
    }
    
    setupHoverEvents() {
        // Slot 1 hover events
        this.slot1Area.on('pointerover', () => {
            if (this.equippedSlot1) { // Only show hover if there's an equipped weapon
                this.slot1Hover.setVisible(true);
                
                // Show tooltip
                const weaponData = itemManager.getItemData(this.equippedSlot1);
                if (weaponData) {
                    this.showTooltip(this.slot1Icon, weaponData.name, 1);
                }
            }
        });
        
        this.slot1Area.on('pointerout', () => {
            this.slot1Hover.setVisible(false);
            this.tooltip.setVisible(false);
        });
        
        // Slot 2 hover events
        this.slot2Area.on('pointerover', () => {
            if (this.equippedSlot2) { // Only show hover if there's an equipped weapon
                this.slot2Hover.setVisible(true);
                
                // Show tooltip
                const weaponData = itemManager.getItemData(this.equippedSlot2);
                if (weaponData) {
                    this.showTooltip(this.slot2Icon, weaponData.name, 2);
                }
            }
        });
        
        this.slot2Area.on('pointerout', () => {
            this.slot2Hover.setVisible(false);
            this.tooltip.setVisible(false);
        });
    }
    
    setupClickEvents() {
        // WeaponUI slots are now display-only - no click events for weapon usage
        // Weapon usage is handled by the scene's click handlers
        
        // Keep right-click context menu prevention for the slots
        this.slot1Area.on('pointerdown', (pointer) => {
            if (pointer.rightButtonDown()) {
                pointer.event.preventDefault(); // Prevent browser context menu
            }
        });
        
        this.slot2Area.on('pointerdown', (pointer) => {
            if (pointer.rightButtonDown()) {
                pointer.event.preventDefault(); // Prevent browser context menu
            }
        });
    }
    
    // Method to update equipped weapon display
    updateEquippedWeapon(slotNumber, weaponItemCode) {        
        // Prevent duplicate weapons - if weapon is already equipped in other slot, unequip it
        if (weaponItemCode) {
            if (slotNumber === 1 && this.equippedSlot2 === weaponItemCode) {
                this.unequipWeapon(2);
            } else if (slotNumber === 2 && this.equippedSlot1 === weaponItemCode) {
                this.unequipWeapon(1);
            }
        }

        if (slotNumber === 1) {
            // Remove existing slot 1 icon if present
            if (this.slot1Icon) {
                this.slot1Icon.destroy();
                this.slot1Icon = null;
            }
            
            this.equippedSlot1 = weaponItemCode;
            
            // Create new icon if weapon is equipped
            if (weaponItemCode) {
                this.slot1Icon = this.createEquippedIcon(weaponItemCode, this.slot1X, this.slot1Y);
            }
            
        } else if (slotNumber === 2) {
            // Remove existing slot 2 icon if present
            if (this.slot2Icon) {
                this.slot2Icon.destroy();
                this.slot2Icon = null;
            }
            
            this.equippedSlot2 = weaponItemCode;
            
            // Create new icon if weapon is equipped
            if (weaponItemCode) {
                this.slot2Icon = this.createEquippedIcon(weaponItemCode, this.slot2X, this.slot2Y);
            }
        }
        this.updateDebugVisuals();
    }
    
    createEquippedIcon(weaponItemCode, x, y) {
        // Get weapon data from items.json
        const weaponData = itemManager.getItemData(weaponItemCode);
        if (!weaponData) {
            console.warn(`No weapon data found for: ${weaponItemCode}`);
            return null;
        }
        
        // Use textureKey from items.json - this should be loaded via spritePath
        let textureKey = weaponData.textureKey;
        
        // Fallback if textureKey doesn't exist in loaded textures
        if (!this.scene.textures.exists(textureKey)) {
            textureKey = weaponData.subtype === 'Ranged' ? 'weapon_bow_basic' : 'weapon_sword_basic';
        }
        let icon;
        // Create the icon sprite
        if (weaponItemCode === 'Weapon_4') {
            icon = this.scene.add.image(x, y, textureKey, 1);
        } else {
            icon = this.scene.add.image(x, y, textureKey);
        }
        icon.setOrigin(0.5, 0.5);
        icon.setScrollFactor(0);
        icon.setDepth(510); // Above UI (500) and hover (508)
        
        return icon;
    }
    
    unequipWeapon(slotNumber) {
        if (slotNumber === 1) {
            if (this.slot1Icon) {
                this.slot1Icon.destroy();
                this.slot1Icon = null;
            }
            this.equippedSlot1 = null;
        } else if (slotNumber === 2) {
            if (this.slot2Icon) {
                this.slot2Icon.destroy();
                this.slot2Icon = null;
            }
            this.equippedSlot2 = null;
        }
        this.updateDebugVisuals();
    }
    
    equipDefaultWeapons() {
        // Equip Basic Sword (Weapon_0) to slot 1 and Basic Bow (Weapon_4) to slot 2
        this.updateEquippedWeapon(1, 'Weapon_0');
        this.updateEquippedWeapon(2, 'Weapon_4');
    }
    
    // Method to get currently equipped weapons
    getEquippedWeapons() {
        return {
            slot1: this.equippedSlot1,
            slot2: this.equippedSlot2
        };
    }
    
    // Weapon usage methods moved to Combat1Scene
    // WeaponUI is now display-only
    
    setupTransparencySystem() {
        // Similar to ToolUI transparency system
        this.transparencyTimer = this.scene.time.addEvent({
            delay: 100,
            callback: this.updateTransparency,
            callbackScope: this,
            loop: true
        });
    }
    
    updateTransparency() {
        const player = this.scene.player;
        if (!player) return;
        
        // Check if player is near weapon UI
        const uiCenterX = this.weaponUIX + this.scene.cameras.main.scrollX;
        const uiCenterY = this.weaponUIY + this.scene.cameras.main.scrollY;
        const distance = Phaser.Math.Distance.Between(player.x, player.y, uiCenterX, uiCenterY);
        
        this.targetAlpha = distance < 50 ? 0.3 : 1.0;
        
        // Smooth alpha transition
        if (Math.abs(this.currentAlpha - this.targetAlpha) > 0.01) {
            this.currentAlpha = Phaser.Math.Linear(this.currentAlpha, this.targetAlpha, 0.1);
            this.setAlpha(this.currentAlpha);
            this.isTransparent = this.currentAlpha < 0.5;
        }
    }
    
    setupTooltip() {
        this.tooltip = this.scene.add.container(0, 0);
        this.tooltip.setAlpha(0.9);
        this.tooltip.setDepth(520); // Above everything else
        this.tooltip.setScrollFactor(0);
        
        this.tooltipBg = this.scene.add.rectangle(0, 0, 100, 20, 0x78716C)
            .setOrigin(0)
            .setStrokeStyle(2, 0xffffff);
        this.tooltip.add(this.tooltipBg);
        
        this.tooltipText = this.scene.add.text(3, 3, '', { 
            fontSize: '12px',
            fontStyle: 'bold',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.tooltip.add(this.tooltipText);
        this.tooltip.setVisible(false);
    }
    
    showTooltip(icon, weaponName, slotNumber) {
        if (!icon) return; // Safety check
        
        const weaponId = slotNumber === 1 ? this.equippedSlot1 : this.equippedSlot2;
        const weaponData = itemManager.getItemData(weaponId);
        const cooldown = slotNumber === 1 ? this.slot1Cooldown : this.slot2Cooldown;
        
        let tooltipText = weaponName;
        if (weaponData) {
            if (weaponData.damage) tooltipText += `\nDamage: ${weaponData.damage}`;
            if (weaponData.speed) tooltipText += `\nSpeed: ${weaponData.speed}`;
            if (weaponData.range) tooltipText += `\nRange: ${weaponData.range}`;
        }
        if (cooldown > 0) {
            tooltipText += `\nCooldown: ${Math.ceil(cooldown/1000)}s`;
        }
        
        this.tooltipText.setText(tooltipText);
        
        const textWidth = this.tooltipText.width;
        this.tooltipBg.setSize(textWidth + 6, this.tooltipText.height + 6);
        
        // Position tooltip above the icon
        let tooltipX = icon.x - (textWidth / 2);
        let tooltipY = icon.y - 50;
        
        // Special adjustment for slot 2 (right slot) - shift left by 5px
        if (slotNumber === 2) {
            tooltipX -= 5;
        }
        
        // Keep tooltip within viewport bounds
        const UI_WIDTH = 256;
        const UI_HEIGHT = 192;
        
        if (tooltipX < 5) tooltipX = 5;
        if (tooltipX + textWidth > UI_WIDTH - 5) tooltipX = UI_WIDTH - textWidth - 5;
        if (tooltipY < 5) tooltipY = icon.y + 20; // Show below if no room above
        
        this.tooltip.setPosition(tooltipX, tooltipY);
        this.tooltip.setVisible(true);
    }
    
    updateDebugVisuals() {
        if (!this.debugGraphics) return;
        
        this.debugGraphics.clear();
        
        if (this.debugMode) {
            // Draw slot areas
            this.debugGraphics.lineStyle(2, 0xff0000, 0.8);
            this.debugGraphics.fillStyle(0xff0000, 0.1);
            
            const slot1Rect = new Phaser.Geom.Rectangle(
                this.slot1X - this.slotSize / 2,
                this.slot1Y - this.slotSize / 2,
                this.slotSize,
                this.slotSize
            );
            this.debugGraphics.strokeRectShape(slot1Rect);
            this.debugGraphics.fillRectShape(slot1Rect);
            
            const slot2Rect = new Phaser.Geom.Rectangle(
                this.slot2X - this.slotSize / 2,
                this.slot2Y - this.slotSize / 2,
                this.slotSize,
                this.slotSize
            );
            this.debugGraphics.strokeRectShape(slot2Rect);
            this.debugGraphics.fillRectShape(slot2Rect);
        }
    }
    
    update(deltaTime) {
        // Update cooldowns
        if (this.slot1Cooldown > 0) {
            this.slot1Cooldown = Math.max(0, this.slot1Cooldown - deltaTime);
        }
        if (this.slot2Cooldown > 0) {
            this.slot2Cooldown = Math.max(0, this.slot2Cooldown - deltaTime);
        }
    }
    
    destroy() {
        if (this.transparencyTimer) {
            this.transparencyTimer.remove();
        }
        if (this.tooltip) {
            this.tooltip.destroy();
        }
        if (this.debugGraphics) {
            this.debugGraphics.destroy();
        }
        if (this.slot1Icon) {
            this.slot1Icon.destroy();
        }
        if (this.slot2Icon) {
            this.slot2Icon.destroy();
        }
        super.destroy();
    }
} 